import { useCallback, useState } from "react";
import { api } from "../../lib/api";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import { REVIEW_PATHS } from "../contract";
import { apiErrorCode } from "../../lib/apiErrors";

/** The one refusal this page answers with its own panel instead of `LoadFailure`. */
const NOT_FOUND_CODE = "PRACTITIONER_NOT_FOUND";

/**
 * `GET /marketplace/practitioners/{slug}` — the practitioner's profile page header,
 * its full service list and its rating summary, as ONE read.
 *
 * THREE OUTCOMES, ALL FIRST-CLASS. `useAdminLoad` already separates "loaded" from
 * "could not load" (BUG-051); this hook adds the third answer a profile URL can get,
 * "there is no such practice" (`404 PRACTITIONER_NOT_FOUND`), and surfaces it as
 * `notFound` rather than as a load failure: a retry button on a slug that does not exist
 * is a dead end dressed as a control, and the page owes the visitor a different sentence.
 * Any OTHER failure — including a 404 without the contract's code, which is what the
 * production proxy's index.html answer looks like — stays a `loadError`.
 *
 * SHAPE-GUARDED: a 200 without `services[]` and `rating_summary` is not a profile and is
 * refused with `payloadShapeError` rather than rendered as an empty practice.
 *
 * @param {string} slug the practitioner's marketplace slug from the route
 */
export default function usePractitionerProfile(slug) {
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setNotFound(false);
    let data;
    try {
      ({ data } = await api.get(REVIEW_PATHS.practitioner(slug)));
    } catch (e) {
      if (e?.response?.status === 404 && apiErrorCode(e) === NOT_FOUND_CODE) {
        setNotFound(true);
        setDetail(null);
        return;
      }
      throw e;
    }
    if (!data || typeof data !== "object" || !Array.isArray(data.services)
        || !data.rating_summary || typeof data.rating_summary !== "object") {
      throw payloadShapeError();
    }
    setDetail(data);
  }, [slug]);

  const { loading, loadError, reload } = useAdminLoad(load);

  return { detail, notFound, loading, loadError, reload };
}
